import React, { useEffect, useRef, useState } from "react";
import { gapi } from "gapi-script";
import { Calendar as BigCalendar, dateFnsLocalizer, Event as BigCalendarEvent, Views } from 'react-big-calendar';
import { format } from 'date-fns';
import { parse } from 'date-fns';
import { startOfWeek } from 'date-fns';
import { getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import Loader from "../../components/Loader";
import toast from "react-hot-toast";
// import GmailCalendarWidget from "./GmailCalendarWidget";

const CLIENT_ID =
  "2342430703-5ub2n448mk49pj76b5r4q0sse409ld2c.apps.googleusercontent.com";
const API_KEY = "AIzaSyBFfe32kMVYB6-OQ1OTGOLU8fGnoli7gBY";
const SCOPES = "https://www.googleapis.com/auth/calendar";
const DISCOVERY_DOC =
  "https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest";

// Setup react-big-calendar localizer
const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface CalendarEvent {
  summary: string;
  start: {
    dateTime?: string;
    date?: string;
  };
  description?: string;
  id?: string;
}

interface TaskEvent {
  _id: string;
  description: string;
  task: string;
  priority: string;
  assignedTo: {
    _id: string;
    name: string;
    email: string;
  };
  createdBy: string;
  dueDate: string;
  status: string;
  createdAt: string;
  projectId?: any;
}

// New interface for Calendar Tasks
interface CalendarTask {
  _id: string;
  title: string;
  description: string;
  dueDate: string;
  followupDateTime?: string; // Updated to match API response
  followupNotes?: string; // Updated to match API response
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  assignedTo?: string;
  status?: string; // Made optional since it's not in the response
  createdAt: string;
  updatedAt: string;
}

interface User {
  _id: string;
  name: string;
  email: string;
}

interface NewEventForm {
  title: string;
  description: string;
  dueDate: string;
  followupDateTime: string;
  followupNotes: string;
  assignedTo: string;
  projectId: string;
}

/**
 * Calendar Component
 * 
 * Features:
 * - Fetches all employees using getEmployees API (with fallback to getAllEmployees)
 * - Admin users can select which employee's tasks to view via dropdown
 * - Events are created and assigned to selected users via userID
 * - Shows username of the user who created each event in the table
 * - Shows username of the user assigned to each event
 * - Google Calendar integration for syncing events
 * - Support for both personal and admin views
 * - Access Control: Only admin users can see all events, others see only their own
 */
const Calendar: React.FC = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [taskEvents, setTaskEvents] = useState<TaskEvent[]>([]);
  const [calendarTasks, setCalendarTasks] = useState<CalendarTask[]>([]);
  console.log("calendarTasks=====>>> ", calendarTasks);
  const [authorized, setAuthorized] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string>("");
  const [calendarUrl, setCalendarUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [users, setUsers] = useState<User[]>([]);
  const [viewMode, setViewMode] = useState<'personal' | 'admin'>('personal');
  const [autoConnect, setAutoConnect] = useState<boolean>(false);
  const [calendarView, setCalendarView] = useState<'list' | 'calendar'>('calendar');
  
  // New state for date selection and event creation
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showEventModal, setShowEventModal] = useState<boolean>(false);
  const [newEventForm, setNewEventForm] = useState<NewEventForm>({
    title: '',
    description: '',
    dueDate: '',
    followupDateTime: '',
    followupNotes: '',
    assignedTo: '',
    projectId: ''
  });
  const [projects, setProjects] = useState<any[]>([]);
  const [creatingEvent, setCreatingEvent] = useState<boolean>(false);

  console.log("events=====>>> ", events);

  const tokenClientRef = useRef<null | ReturnType<
    typeof window.google.accounts.oauth2.initTokenClient
  >>(null);
  const gapiInited = useRef<boolean>(false);
  const gisInited = useRef<boolean>(false);

  // Helper function to get user name by ID
  const getUserName = (userId: string): string => {
    const user = users.find(user => user._id === userId);
    return user ? user.name : 'Unknown User';
  };

  // Helper function to get user name from createdBy object
  const getCreatedByUserName = (createdBy: { _id: string; name: string; email: string }): string => {
    return createdBy?.name || 'Unknown User';
  };

  // Convert events to react-big-calendar format
  const getBigCalendarEvents = (): BigCalendarEvent[] => {
    const bigCalendarEvents: BigCalendarEvent[] = [];

    // Add Gmail calendar events
    events.forEach((event) => {
      const startDate = event.start?.dateTime ? new Date(event.start.dateTime) : 
                       event.start?.date ? new Date(event.start.date) : new Date();
      const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour duration

      bigCalendarEvents.push({
        title: event.summary || 'Gmail Event',
        start: startDate,
        end: endDate,
        resource: { type: 'gmail', description: event.description }
      } as BigCalendarEvent);
    });

    // Add calendar task events
    calendarTasks.forEach((task) => {
      const startDate = new Date(task.dueDate);
      const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour duration

      bigCalendarEvents.push({
        title: task.title,
        start: startDate,
        end: endDate,
        resource: { 
          type: 'calendarTask', 
          description: task.description,
          followupNotes: task.followupNotes,
          followupDateTime: task.followupDateTime
        }
      } as BigCalendarEvent);
    });

    return bigCalendarEvents;
  };

  // Event style function for different event types
  const eventStyleGetter = (event: BigCalendarEvent) => {
    const resource = event.resource as any;
    
    if (resource?.type === 'gmail') {
      return {
        style: {
          backgroundColor: '#3B82F6',
          color: 'white',
          borderRadius: '4px',
          border: 'none'
        }
      };
    } else if (resource?.type === 'calendarTask') {
      return {
        style: {
          backgroundColor: '#10B981',
          color: 'white',
          borderRadius: '4px',
          border: 'none'
        }
      };
    }

    return {};
  };

  // Get current user info from localStorage and auto-connect Gmail
  useEffect(() => {
    const role = localStorage.getItem("role");
    const userId = localStorage.getItem("userId");
    const userEmail = localStorage.getItem("userEmail");
    
    console.log("Calendar: Retrieved from localStorage:", { role, userId, userEmail });
    
    if (userId && role) {
      setCurrentUser({ role, userId });
      
      // Security: Only admin users can access admin view
      if (role === "admin") {
        setViewMode('admin');
        fetchUsers();
        console.log('Admin user detected, enabling admin view');
      } else {
        setViewMode('personal');
        console.log('Non-admin user detected, restricting to personal view');
      }

      // Auto-connect to Gmail if user email is available
      if (userEmail) {
        console.log("Calendar: Setting user email:", userEmail);
        setUserEmail(userEmail);
        setAutoConnect(true);
      } else {
        console.log("Calendar: No user email found in localStorage");
        // Try to get email from the login username field
        const loginUsername = localStorage.getItem("loginUsername");
        if (loginUsername) {
          console.log("Calendar: Using login username as email:", loginUsername);
          setUserEmail(loginUsername);
          setAutoConnect(true);
        }
      }
    }
  }, []);

  // Fetch all users for admin view
  const fetchUsers = async () => {
    try {
      const response = await ApiService({
        method: "GET",
        endpoint: apiPaths.getAllEmployees,
      });
      setUsers(response.users || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  // Fetch projects for dropdown
  useEffect(() => {
    fetchProjects();
  }, []);

  // Fetch calendar tasks based on user role and selected user
  const fetchCalendarTasks = async (userId?: string) => {
    setLoading(true);
    try {
      let endpoint = apiPaths.getCalendarTasks;
      
      if (currentUser?.role !== "admin") {
        // For non-admin users, fetch only their own tasks
        endpoint = `${apiPaths.getCalendarTasks}?userId=${currentUser?.userId}`;
        console.log('Non-admin user fetching own tasks for userId:', currentUser?.userId);
      } else if (userId && userId !== "all") {
        // For admin viewing specific user's tasks
        endpoint = `${apiPaths.getCalendarTasks}?userId=${userId}`;
        console.log('Admin fetching tasks for specific user:', userId);
      } else {
        // For admin viewing all tasks
        console.log('Admin fetching all tasks');
      }
      
      const response = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });

      const tasks = response.tasks || response.calendarTasks || [];
      console.log('Fetched tasks:', tasks);
      
      // Filter tasks based on user role for security
      let filteredTasks = tasks;
      if (currentUser?.role !== "admin") {
        // Non-admin users can only see tasks they created or are assigned to
        filteredTasks = tasks.filter((task: CalendarTask) => 
          task.createdBy._id === currentUser?.userId || 
          task.assignedTo === currentUser?.userId
        );
        console.log('Filtered tasks for non-admin user:', filteredTasks);
      }
      
      setCalendarTasks(filteredTasks);
      
      // Convert tasks to calendar events for Gmail
      const calendarEvents = filteredTasks.map((task: CalendarTask) => ({
        summary: task.title,
        start: {
          dateTime: task.dueDate,
        },
        description: task.description,
        id: task._id,
      }));
      
      setEvents(calendarEvents);
    } catch (error) {
      console.error("Failed to fetch calendar tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch calendar tasks when user selection changes
  useEffect(() => {
    if (currentUser) {
      if (viewMode === 'personal') {
        fetchCalendarTasks();
      } else if (viewMode === 'admin') {
        fetchCalendarTasks(selectedUser);
      }
    }
  }, [currentUser, selectedUser, viewMode]);

  // Auto-connect to Gmail when component mounts
  useEffect(() => {
    console.log("Auto-connect effect triggered:", { autoConnect, userEmail });
    
    if (autoConnect && userEmail) {
      console.log("Auto-connecting to Gmail with email:", userEmail);
      // Create calendar URL for the user's email
      const encodedEmail = encodeURIComponent(userEmail);
      const calendarUrl = `https://calendar.google.com/calendar/embed?src=${encodedEmail}&ctz=${
        Intl.DateTimeFormat().resolvedOptions().timeZone
      }`;
      console.log("Generated calendar URL:", calendarUrl);
      setCalendarUrl(calendarUrl);
      setAuthorized(true);
    } else if (autoConnect && !userEmail) {
      console.log("Auto-connect enabled but no user email available");
    }
  }, [autoConnect, userEmail]);

  useEffect(() => {
    const loadGapiScript = () => {
      const script = document.createElement("script");
      script.src = "https://apis.google.com/js/api.js";
      script.async = true;
      script.defer = true;
      script.onload = gapiLoaded;
      document.body.appendChild(script);
    };

    const loadGisScript = () => {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = gisLoaded;
      document.body.appendChild(script);
    };

    loadGapiScript();
    loadGisScript();
  }, []);

  const gapiLoaded = () => {
    gapi.load("client", initializeGapiClient);
  };

  const initializeGapiClient = async () => {
    await gapi.client.init({
      apiKey: API_KEY,
      discoveryDocs: [DISCOVERY_DOC],
    });
    gapiInited.current = true;
    maybeEnableButtons();
  };

  const gisLoaded = () => {
    tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPES,
      callback: (tokenResponse: google.accounts.oauth2.TokenResponse) => {
        console.log("Token response:", tokenResponse);
      },
    });
    gisInited.current = true;
    maybeEnableButtons();
  };

  const maybeEnableButtons = () => {
    if (gapiInited.current && gisInited.current) {
      // Ready to authorize
    }
  };

  const handleAuthClick = () => {
    if (!tokenClientRef.current) return;

    tokenClientRef.current.callback = async (
      resp: google.accounts.oauth2.TokenResponse
    ) => {
      if (resp.error) {
        console.error('Google OAuth error:', resp);
        toast.error('Failed to connect to Google Calendar. Please try again.');
        return;
      }
      
      console.log('Google OAuth successful:', resp);
      setAuthorized(true);
      
      try {
        await getUserInfo();
        await listUpcomingEvents();
        
        // Test Google Calendar access
        await testGoogleCalendarAccess();
        
        toast.success('Successfully connected to Google Calendar! You can now create events that will sync to your Google Calendar.');
      } catch (error) {
        console.error('Error after Google OAuth:', error);
        toast.error('Connected to Google but failed to access calendar. Please try refreshing.');
      }
    };

    if (gapi.client.getToken() === null) {
      console.log('Requesting new Google OAuth token...');
      tokenClientRef.current.requestAccessToken({ prompt: "consent" });
    } else {
      console.log('Refreshing existing Google OAuth token...');
      tokenClientRef.current.requestAccessToken({ prompt: "" });
    }
  };

  // Test Google Calendar access
  const testGoogleCalendarAccess = async () => {
    try {
      console.log('Testing Google Calendar access...');
      
      // Try to list calendars to verify access
      const calendarsResponse = await gapi.client.calendar.calendarList.list();
      console.log('Available calendars:', calendarsResponse.result.items);
      
      // Try to create a test event (will be deleted immediately)
      const testEvent = {
        'summary': 'Test Event - Will be deleted',
        'description': 'This is a test event to verify Google Calendar access',
        'start': {
          'dateTime': new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Tomorrow
          'timeZone': Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        'end': {
          'dateTime': new Date(Date.now() + 24 * 60 * 60 * 1000 + 60 * 60 * 1000).toISOString(), // Tomorrow + 1 hour
          'timeZone': Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };

      const createResponse = await gapi.client.calendar.events.insert({
        calendarId: 'primary',
        resource: testEvent,
      });

      console.log('Test event created successfully:', createResponse.result);

      // Delete the test event immediately
      await gapi.client.calendar.events.delete({
        calendarId: 'primary',
        eventId: createResponse.result.id,
      });

      console.log('Test event deleted successfully');
      console.log('Google Calendar access verified successfully!');
      
    } catch (error) {
      console.error('Failed to test Google Calendar access:', error);
      throw error;
    }
  };

  const handleSignoutClick = () => {
    const token = gapi.client.getToken();
    if (token) {
      google.accounts.oauth2.revoke(token.access_token, () => {
        gapi.client.setToken("");
        setAuthorized(false);
        setUserEmail("");
        setCalendarUrl("");
      });
    }
  };

  const getUserInfo = async () => {
    try {
      const response = await gapi.client.oauth2.userinfo.get();
      const userEmail = response.result.email;
      setUserEmail(userEmail);

      const encodedEmail = encodeURIComponent(userEmail);
      const calendarUrl = `https://calendar.google.com/calendar/embed?src=${encodedEmail}&ctz=${
        Intl.DateTimeFormat().resolvedOptions().timeZone
      }`;
      setCalendarUrl(calendarUrl);
    } catch (error) {
      console.error("Failed to get user info:", error);
    }
  };

  const listUpcomingEvents = async () => {
    try {
      const response = await gapi.client.calendar.events.list({
        calendarId: "primary",
        timeMin: new Date().toISOString(),
        showDeleted: false,
        singleEvents: true,
        maxResults: 10,
        orderBy: "startTime",
      });

      const fetchedEvents: CalendarEvent[] = response.result.items || [];
      setEvents(fetchedEvents);
    } catch (err) {
      console.error("Failed to fetch events", err);
    }
  };

  // Create event in Google Calendar
  const createGoogleCalendarEvent = async (taskData: any) => {
    try {
      // Ensure we have a valid date
      let startDateTime = taskData.dueDate;
      if (!startDateTime) {
        console.error('No due date provided for Google Calendar event');
        return null;
      }

      // Convert to proper ISO string if it's not already
      if (typeof startDateTime === 'string') {
        const date = new Date(startDateTime);
        if (isNaN(date.getTime())) {
          console.error('Invalid date format:', startDateTime);
          return null;
        }
        startDateTime = date.toISOString();
      }

      // Calculate end time (1 hour after start)
      const endDateTime = new Date(new Date(startDateTime).getTime() + 60 * 60 * 1000).toISOString();

      const event = {
        'summary': taskData.title || 'Calendar Task',
        'description': taskData.description || 'No description',
        'start': {
          'dateTime': startDateTime,
          'timeZone': Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        'end': {
          'dateTime': endDateTime,
          'timeZone': Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        'reminders': {
          'useDefault': false,
          'overrides': [
            {'method': 'email', 'minutes': 24 * 60},
            {'method': 'popup', 'minutes': 30},
          ],
        },
        'colorId': '1', // Blue color
        'transparency': 'opaque',
      };

      console.log('Creating Google Calendar event with data:', event);

      const response = await gapi.client.calendar.events.insert({
        calendarId: 'primary',
        resource: event,
        sendUpdates: 'all', // Send notifications to attendees
      });

      console.log('Event created in Google Calendar:', response.result);
      
      // Refresh the events list to show the new event
      await listUpcomingEvents();
      
      return response.result.id;
    } catch (error) {
      console.error('Failed to create Google Calendar event:', error);
      console.error('Error details:', (error as any).result || error);
      throw error;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'finished':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle date selection from calendar
  const handleSelectSlot = (slotInfo: any) => {
    setSelectedDate(slotInfo.start);
    setNewEventForm(prev => ({
      ...prev,
      dueDate: slotInfo.start.toISOString().split('T')[0]
    }));
    setShowEventModal(true);
  };

  // Handle event click
  const handleSelectEvent = (event: BigCalendarEvent) => {
    console.log('Selected event:', event);
    // You can add event editing functionality here
  };

  // Fetch projects for the dropdown
  const fetchProjects = async () => {
    try {
      const response = await ApiService({
        method: "GET",
        endpoint: apiPaths.getProjects,
      });
      setProjects(response || []);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    }
  };

  // Handle form input changes
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewEventForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Create new calendar event/task
  const handleCreateEvent = async () => {
    if (!newEventForm.title || !newEventForm.description || !newEventForm.dueDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreatingEvent(true);
    try {
      const eventData = {
        title: newEventForm.title,
        description: newEventForm.description,
        dueDate: newEventForm.dueDate,
        followupDateTime: newEventForm.followupDateTime || newEventForm.dueDate,
        followupNotes: newEventForm.followupNotes,
        assignedTo: newEventForm.assignedTo || currentUser?.userId,
        createdBy: currentUser?.userId, // Add the current user as the creator
        projectId: newEventForm.projectId,
        status: 'pending'
      };

      console.log('Creating calendar event with data:', eventData);

      // First, create the event in the database
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.createCalendarTask,
        data: eventData,
      });

      console.log('Calendar task created in database:', response);

      // Then, create the event in Google Calendar if authorized
      let googleCalendarSuccess = false;
      if (authorized && gapi.client.getToken()) {
        try {
          console.log('Attempting to create Google Calendar event...');
          const googleEventId = await createGoogleCalendarEvent(eventData);
          if (googleEventId) {
            console.log('Event successfully created in Google Calendar with ID:', googleEventId);
            googleCalendarSuccess = true;
          }
        } catch (googleError) {
          console.error('Failed to create Google Calendar event:', googleError);
          // Don't fail the whole operation, just log the error
        }
      } else {
        console.log('Google Calendar not authorized or no token available');
      }

      // Refresh calendar tasks after creating new event
      await fetchCalendarTasks(currentUser?.role === "admin" ? selectedUser : undefined);
      
      // Reset form and close modal
      setNewEventForm({
        title: '',
        description: '',
        dueDate: '',
        followupDateTime: '',
        followupNotes: '',
        assignedTo: '',
        projectId: ''
      });
      setShowEventModal(false);
      setSelectedDate(null);
      
      // Show success message with Google Calendar status
      if (googleCalendarSuccess) {
        toast.success('Calendar event created successfully in both database and Google Calendar!');
      } else {
        toast.error('Calendar event created successfully in database! Google Calendar integration failed.');
      }
    } catch (error) {
      console.error("Failed to create calendar event:", error);
      toast.error('Failed to create calendar event. Please try again.');
    } finally {
      setCreatingEvent(false);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setShowEventModal(false);
    setSelectedDate(null);
    setNewEventForm({
      title: '',
      description: '',
      dueDate: '',
      followupDateTime: '',
      followupNotes: '',
      assignedTo: '',
      projectId: ''
    });
  };

  return (
    <div className="p-6 h-[85vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Calendar Tasks</h1>
        
        {/* View Mode Toggle for Admin Only */}
        {/* {currentUser?.role === "admin" ? (
          <div className="flex items-center space-x-4">
            <div className="flex bg-gray-200 rounded-lg p-1">
              <button
                onClick={() => setViewMode('personal')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'personal'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                My Tasks
              </button>
              <button
                onClick={() => setViewMode('admin')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'admin'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All Users
              </button>
            </div>
            
            {viewMode === 'admin' && (
              <div className="flex items-center space-x-2">
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                >
                  <option value="all">All Users</option>
                  {loading ? (
                    <option value="" disabled>Loading users...</option>
                  ) : (
                    users.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.name} ({user.email})
                      </option>
                    ))
                  )}
                </select>
                <button
                  onClick={fetchUsers}
                  disabled={loading}
                  className="px-2 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                  title="Refresh employee list"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                {loading && (
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                )}
                {!loading && users.length === 0 && (
                  <span className="text-xs text-red-500">No users found</span>
                )}
              </div>
            )}
          </div>
        ) : 
        (
          <div className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-md">
            Personal View - You can only see your own events
          </div>
        )} */}
      </div>

      {/* View Toggle and Quick Add Button */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex bg-gray-200 rounded-lg p-1">
          <button
            onClick={() => setCalendarView('calendar')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              calendarView === 'calendar'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Calendar View
          </button>
          <button
            onClick={() => setCalendarView('list')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              calendarView === 'list'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            List View
          </button>
        </div>
        
        {/* Quick Add Button */}
        <button
          onClick={() => {
            setNewEventForm(prev => ({
              ...prev,
              dueDate: new Date().toISOString().split('T')[0]
            }));
            setShowEventModal(true);
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-2"
        >
          <span>+</span>
          <span>Quick Add Event</span>
        </button>
      </div>

      {/* Google Calendar Integration */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
       
          <div className="space-x-2">
            <button
              onClick={handleAuthClick}
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors"
            >
              {authorized ? "Connect" : "Connect Google Calendar"}
            </button>
            <button
              onClick={() => {
                const email = localStorage.getItem("userEmail") || localStorage.getItem("loginUsername");
                if (email) {
                  setUserEmail(email);
                  setAutoConnect(true);
                }
              }}
              className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition-colors"
            >
              Auto-Connect
            </button>
            {/* {authorized && (
              <>
                <button
                  onClick={testGoogleCalendarAccess}
                  className="bg-purple-500 text-white px-4 py-2 rounded-md hover:bg-purple-600 transition-colors"
                >
                  Test Connection
                </button>
                <button
                  onClick={handleSignoutClick}
                  className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition-colors"
                >
                  Disconnect
                </button>
              </>
            )} */}
          </div>
        </div>

        {/* Google Calendar Status */}
        {/* {authorized && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center space-x-2">
              <span className="text-green-600">✓</span>
              <p className="text-sm text-green-800">
                <strong>Google Calendar Integration Active</strong>
              </p>
            </div>
            <p className="text-sm text-green-700 mt-1">
              New calendar tasks will automatically appear in your Google Calendar
            </p>
          </div>
        )} */}

        {/* Troubleshooting Info */}
        {/* {!authorized && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>To sync with Google Calendar:</strong>
            </p>
            <ol className="text-sm text-yellow-700 mt-1 list-decimal list-inside space-y-1">
              <li>Click "Connect Google Calendar" above</li>
              <li>Sign in with your Google account</li>
              <li>Grant calendar permissions when prompted</li>
              <li>Create calendar tasks - they'll sync automatically!</li>
            </ol>
          </div>
        )} */}

      

        {/* {calendarUrl && (
          <iframe
            src={calendarUrl}
            width="100%"
            height="400"
            frameBorder="0"
            scrolling="no"
            title="Google Calendar"
          />
        )} */}
      </div>

      {/* Calendar View */}
      {calendarView === 'calendar' && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4">
            {viewMode === 'personal' ? 'Calendar' : 'All Calendar Tasks'}
          </h2>
          
          
          
          {loading ? (
            <Loader />
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div style={{ height: 600 }}>
                <BigCalendar
                  localizer={localizer}
                  events={getBigCalendarEvents()}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: 550 }}
                  eventPropGetter={eventStyleGetter}
                  views={['month', 'week', 'day']}
                  defaultView="month"
                  selectable={true}
                  onSelectSlot={handleSelectSlot}
                  onSelectEvent={handleSelectEvent}
                  popup={true}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* List View */}
      {calendarView === 'list' && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4">
            {viewMode === 'personal' ? 'My Calendar Tasks' : 'All Calendar Tasks'}
          </h2>
          
          {loading ? (
            <Loader />
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {calendarTasks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No calendar tasks found.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Due Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Followup Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Followup Notes
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assigned To
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created By
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {calendarTasks.map((task) => (
                        <tr key={task._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {task.title}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs truncate">
                              {task.description}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatDate(task.dueDate)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {task.followupDateTime ? formatDate(task.followupDateTime) : 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs truncate">
                              {task.followupNotes || 'No notes'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(task.status || 'pending')}`}>
                              {task.status || 'pending'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {task.assignedTo ? (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                  {getUserName(task.assignedTo)}
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                                  Unassigned
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                {getCreatedByUserName(task.createdBy)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Calendar Events from Google Calendar */}
      {authorized && events.length > 0 && calendarView === 'list' && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4">Google Calendar Events</h2>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Event Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Start Date/Time
                    </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created By
                        </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {events.map((event, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {event.summary || 'Untitled Event'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {event.description || 'No description'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {event.start?.dateTime ? 
                            formatDate(event.start.dateTime) : 
                            event.start?.date ? 
                              formatDate(event.start.date) : 
                              'No date'
                          }
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 rounded-full text-xs font-medium border border-blue-200 bg-blue-50 text-blue-800">
                          {event.start?.dateTime ? 'Event' : 'All Day'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                            {getUserName(currentUser?.userId)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Event Creation Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Create New Calendar Task</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); handleCreateEvent(); }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={newEventForm.title}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter task title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={newEventForm.description}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter task description"
                    rows={3}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date *
                  </label>
                  <input
                    type="datetime-local"
                    name="dueDate"
                    value={newEventForm.dueDate}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Followup Date/Time
                  </label>
                  <input
                    type="datetime-local"
                    name="followupDateTime"
                    value={newEventForm.followupDateTime}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Followup Notes
                  </label>
                  <textarea
                    name="followupNotes"
                    value={newEventForm.followupNotes}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter followup notes"
                    rows={2}
                  />
                </div>

                {currentUser?.role === "admin" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assign To
                    </label>
                    <select
                      name="assignedTo"
                      value={newEventForm.assignedTo}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select User</option>
                      {users.map((user) => (
                        <option key={user._id} value={user._id}>
                          {user.name} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingEvent}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {creatingEvent ? 'Creating...' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
